import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import {
  doCreateRoutePlan,
  doDeleteRoutePlan,
  doDispatchRoutePlan,
  doGetRoutePlan,
  doGetRoutePlans,
  doOptimizeRoutePlan,
  doReorderRoutePlan,
  doUpdateRoutePlan,
  doLockRiderInsideRoutePlan,
  doRemoveOrdersInsideRoutePlan,
  doRemoveRidersInsideRoutePlan,
  doAddOrdersToRoutePlan,
  doSwapOrdersToRiderRoutePlan,
} from './thunk';

const isActionWithPending = (action) => {
  return (
    action.type &&
    action.type.startsWith('routePlans/') &&
    action.type.endsWith('/pending') &&
    !action.type.includes('doLockRiderInsideRoutePlan')
  );
};

const isActionWithFulfilled = (action) => {
  return (
    action.type &&
    action.type.startsWith('routePlans/') &&
    action.type.endsWith('/fulfilled') &&
    !action.type.includes('doLockRiderInsideRoutePlan')
  );
};

const isActionWithFailed = (action) => {
  return (
    action.type &&
    action.type.startsWith('routePlans/') &&
    action.type.endsWith('/rejected') &&
    !action.type.includes('doLockRiderInsideRoutePlan')
  );
};

const routePlansAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

const routePlanSlice = createSlice({
  name: 'routePlan',
  initialState: {
    action: null,
    routePlanDetail: null,
    isFetching: false,
    status: 'idle',
    metadata: {},
    entities: [],
    ids: [],
    idCreate: undefined,
    item: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doGetRoutePlans.pending, (state) => {
      state.action = 'getall';
      state.status = 'loading';
    });
    builder.addCase(doGetRoutePlans.fulfilled, (state, action) => {
      const { routePlans, pagination } = action.payload;
      state.status = 'succeeded';
      state.metadata = pagination;
      state.idCreate = undefined;
      state.routePlanDetail = undefined;
      routePlansAdapter.setAll(state, routePlans);
    });
    builder.addCase(doGetRoutePlans.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetRoutePlan.pending, (state) => {
      state.action = 'getOne';
      state.status = 'loading';
      state.idCreate = undefined;
      // state.routePlanDetail = undefined;
    });
    builder.addCase(doGetRoutePlan.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.routePlanDetail = action.payload;
    });
    builder.addCase(doGetRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doCreateRoutePlan.pending, (state) => {
      state.action = 'create';
      state.status = 'loading';
    });
    builder.addCase(doCreateRoutePlan.fulfilled, (state, action) => {
      state.idCreate = action.payload?.slug;
      state.status = 'succeeded';
    });
    builder.addCase(doCreateRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doOptimizeRoutePlan.pending, (state) => {
      state.action = 'optimize';
      state.status = 'loading';
    });
    builder.addCase(doOptimizeRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doOptimizeRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doUpdateRoutePlan.pending, (state) => {
      state.action = 'update';
      state.status = 'loading';
    });
    builder.addCase(doUpdateRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doUpdateRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doLockRiderInsideRoutePlan.pending, (state) => {
      state.action = 'lock-rider';
      state.status = 'loading';
    });
    builder.addCase(doLockRiderInsideRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
      state.action = null;
    });
    builder.addCase(doLockRiderInsideRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doDeleteRoutePlan.pending, (state) => {
      state.action = 'delete';
      state.status = 'loading';
    });
    builder.addCase(doDeleteRoutePlan.fulfilled, (state, action) => {
      state.status = 'succeeded';
      routePlansAdapter.removeOne(state, action.meta.arg);
    });
    builder.addCase(doDeleteRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doReorderRoutePlan.pending, (state) => {
      state.action = 'reorder';
      state.status = 'loading';
    });
    builder.addCase(doReorderRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doReorderRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doDispatchRoutePlan.pending, (state) => {
      state.action = 'dispatch';
      state.status = 'loading';
    });
    builder.addCase(doDispatchRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doDispatchRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doRemoveOrdersInsideRoutePlan.pending, (state) => {
      state.action = 'remove-orders';
      state.status = 'loading';
    });
    builder.addCase(doRemoveOrdersInsideRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doRemoveOrdersInsideRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doRemoveRidersInsideRoutePlan.pending, (state) => {
      state.action = 'remove-riders';
      state.status = 'loading';
    });
    builder.addCase(doRemoveRidersInsideRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doRemoveRidersInsideRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doAddOrdersToRoutePlan.pending, (state) => {
      state.action = 'add-orders';
      state.status = 'loading';
    });
    builder.addCase(doAddOrdersToRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doAddOrdersToRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doSwapOrdersToRiderRoutePlan.pending, (state) => {
      state.action = 'swap-riders';
      state.status = 'loading';
    });
    builder.addCase(doSwapOrdersToRiderRoutePlan.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doSwapOrdersToRiderRoutePlan.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addMatcher(isActionWithPending, (state) => {
      state.isFetching = true;
      state.message = null;
    });
    builder.addMatcher(isActionWithFulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, (state) => {
      state.isFetching = false;
      state.message = null;
    });
  },
});

export const {
  selectAll: selectAllRoutePlans,
  selectById: selectRoutePlanById,
  selectIds: selectRoutePlanIds,
} = routePlansAdapter.getSelectors((state) => state.routePlan);

const { reducer: routePlanReducer } = routePlanSlice;
export default routePlanReducer;
